/**
 * @fileoverview Entry point for displaying records in the Cloudomation frontend application.
 *
 * This module serves as the central configuration hub for all resource types that can be
 * displayed, created, and managed within the application. It imports individual resource
 * configurations and organizes them into logical groupings for different UI contexts.
 *
 * The module exports:
 * - Complete list of all available resources
 * - Resource lookup by name
 * - Resources for different creation menus
 * - Configuration-specific resources
 *
 * @module config/resources
 */

import { sortAlphabetically } from "utils/sorting";
import bundle from "./resources/bundle";
import bundle_repository from "./resources/bundle_repository";
import connector from "./resources/connector";
import custom_object from "./resources/custom_object";
import dashboard from "./resources/dashboard";
import devolutions_config from "./resources/devolutions_config";
import egress_policy from "./resources/egress_policy";
import execution from "./resources/execution";
import file from "./resources/file";
import flow from "./resources/flow";
import ldap_config from "./resources/ldap_config";
import message from "./resources/message";
import object_template from "./resources/object_template";
import object_template_attribute from "./resources/object_template_attribute";
import mcp_user_tool from "./resources/mcp_user_tool";
import organization from "./resources/organization";
import plugin from "./resources/plugin";
import plugin_action from "./resources/plugin_action";
import project from "./resources/project";
import resource_wrapper from "./resources/resource_wrapper";
import role from "./resources/role";
import role_permission from "./resources/role_permission";
import schedule from "./resources/schedule";
import scheduler from "./resources/scheduler";
import schema from "./resources/schema";
import secret from "./resources/secret";
import setting from "./resources/setting";
import sync_config from "./resources/sync_config";
import user from "./resources/user";
import user_git_public_key from "./resources/user_git_public_key";
import vault_config from "./resources/vault_config";
import webhook from "./resources/webhook";
import workspace from "./resources/workspace";
import wrapper from "./resources/wrapper";
import python_module from "./resources/python_module";
import zone from "./resources/zone";

/**
 * Complete list of all available resources in the application.
 *
 * This array contains all resource configurations that can be displayed,
 * managed, or interacted with throughout the application. Resources are
 * ordered by their typical usage frequency and logical grouping.
 *
 * @type {Array<Object>}
 * @readonly
 */
export const resources = [
    workspace,
    flow,
    setting,
    user,
    webhook,
    file,
    project,
    role_permission,
    role,
    schedule,
    scheduler,
    message,
    execution,
    sync_config,
    ldap_config,
    vault_config,
    organization,
    bundle,
    bundle_repository,
    connector,
    plugin,
    plugin_action,
    schema,
    wrapper,
    python_module,
    resource_wrapper,
    user_git_public_key,
    object_template,
    object_template_attribute,
    custom_object,
    dashboard,
    devolutions_config,
    egress_policy,
    mcp_user_tool,
    secret,
    zone,
];

// TODO (low) CU-2yt784w refactor: remove
export const resourceByName = {
    workspace: workspace,
    flow: flow,
    setting: setting,
    user: user,
    webhook: webhook,
    mcp_user_tool: mcp_user_tool,
    file: file,
    project: project,
    role_permission: role_permission,
    role: role,
    schedule: schedule,
    scheduler: scheduler,
    message: message,
    execution: execution,
    sync_config: sync_config,
    ldap_config: ldap_config,
    vault_config: vault_config,
    organization: organization,
    bundle: bundle,
    bundle_repository: bundle_repository,
    connector: connector,
    plugin: plugin,
    plugin_action: plugin_action,
    schema: schema,
    wrapper: wrapper,
    python_module: python_module,
    resource_wrapper: resource_wrapper,
    user_git_public_key,
    object_template: object_template,
    object_template_attribute,
    custom_object: custom_object,
    dashboard: dashboard,
    devolutions_config,
    egress_policy,
    secret: secret,
    zone,
};

/**
 * Primary resources available in the main "Create" menu.
 */
export const createResources = [flow, connector, project, custom_object];

// Secondary resources for "Create" -> "More" submenu
const createMoreResources = [
    webhook,
    file,
    schedule,
    scheduler,
    schema,
    wrapper,
    python_module,
    bundle,
    object_template,
    setting,
    dashboard,
];

/**
 * Alphabetically sorted list of resources for "Create" -> "More" submenu.
 *
 * @type {Array<ResourceConfig>}
 */
export const createMoreResourcesSorted = sortAlphabetically(
    [...createMoreResources],
    (resource) => resource.resourceName
);

// Resources for "Create" -> "Configuration" submenu
const configurationResources = [
    sync_config,
    ldap_config,
    vault_config,
    plugin,
    bundle_repository,
    role,
    devolutions_config,
    egress_policy,
    mcp_user_tool,
    secret,
    zone,
];

/**
 * Alphabetically sorted list of configuration resources for "Create" -> "Configuration" submenu.
 *
 * @type {Array<ResourceConfig>}
 */
export const configurationResourcesSorted = sortAlphabetically(
    [...configurationResources],
    (resource) => resource.resourceName
);
